// @flow

import React, {useMemo, useState} from 'react';
import {
    ComboboxListbox,
    ComboboxItem, useComboboxValue
} from '@pb/kit-component-combo-box';
import {
    useListbox
} from '@pb/kit-component-listbox';
import EllipsisTextHint from '@pb/kit-component-ellipsis-text-hint';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';
import Icon, {Icons} from '@pb/kit-component-icon';

import styles from './cell-combo-box.module.styl';

import type {StatelessFunctionalComponent as FC} from 'react';


const classes = hashedClasses(styles);
const classNames = withCssModule(styles);

export type CellComboBoxDataType = Array<{|
    value: string,
    caption: string,
    isSegment: boolean,
    isSubsegmented: boolean,
    disabled?: boolean,
|}>;

type Props = {|
    data: CellComboBoxDataType,
    value: string,
    placeholder: string,
    itemsForScroll: number,
    disabled?: boolean,
    onToggle?: (shown: boolean) => void,
    onChange: (value: string, name: string) => void,
|};

/**
 *
 @see https://storybook.dev.pitchbookdata.com/?path=/story/tables-data-table-cells--cell-combo-box-default
 */
export const CellComboBox: FC<Props> = (props: Props) => {
    const {
        data,
        onChange: onChangeProp,
        itemsForScroll,
        disabled,
        value: valueProp,
        placeholder,
        onToggle
    } = props;

    const [shown, _setShown] = useState(false);

    const setShown = (newShown) => {
        if (disabled) {
            return;
        }
        _setShown(newShown);
    }

    const comboBoxItems = useMemo(() => data.map(({value: itemValue, caption, isSegment, isSubsegmented}) => (
        <ComboboxItem
            key={itemValue}
            value={itemValue}
            caption={caption}
            disabled={isSubsegmented}
            className={classNames({'combo-box__item_shifted': !isSegment})}
        />
    )), [data]);

    const {
        value,
        onChange,
    } = useComboboxValue({
        value: valueProp,
        onChange: onChangeProp,
        setShown
    });

    const {
        anchorRef,
        getAnchorProps,
        getListBoxProps
    } = useListbox({
        itemsForScroll,
        onToggle,
        shown,
        setShown
    });

    const selectedItemsProps = value.map(itemValue => data.find(({value: _value}) => _value === itemValue));
    const caption = selectedItemsProps[0]?.caption;

    return (
        <>
            <div
                role="combobox"
                aria-label={placeholder ?? ''}
                aria-disabled={disabled}
                tabIndex={0}
                {...{
                    ...getAnchorProps({
                        onKeyDown: e => {
                            if (e.key === 'Enter') {
                                const {activeIndex} = getListBoxProps();
                                if (typeof activeIndex !== 'number') {
                                    return;
                                }
                                onChange(data[activeIndex]);
                            }
                        }
                    }),
                    className: classNames('cell-combo-box', {
                        'cell-combo-box_disabled': disabled
                    })
                }}
                ref={anchorRef}>
                <EllipsisTextHint>
                    {caption ? caption : (
                        <span className={classes['combo-box__placeholder']}>
                            {placeholder}
                        </span>
                    )}
                </EllipsisTextHint>
                <span className={classNames('combo-box__icon', {
                    'combo-box__icon-shown': shown
                })}>
                    <Icon aria-hidden={true} type={Icons.ANGLE_DOWN}/>
                </span>
            </div>
            <ComboboxListbox
                aria-label={placeholder ?? ''}
                {...{
                    dropdown: true,
                    value,
                    ...getListBoxProps(),
                    onChange,
                }}>
                {comboBoxItems}
            </ComboboxListbox>
        </>
    );
};
